import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useIdleTimer } from 'react-idle-timer';
import { Button, Modal } from '@design-system';
import { useQueryClient } from '@tanstack/react-query';
import { AGREEMENTS_KEY, PROFILE_PICTURE_KEY, USER_KEY } from 'src/utils/constants/queryKeys';
import styles from './InactivityPrompt.module.scss';
import { useIdleStore } from 'src/stores/IdleStore';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';

const TWO_MINUTES_IN_SECONDS = 120;

const formatSeconds = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const InactivityPrompt: React.FC = () => {
  const { timeout, idlePromptOpen, setIdlePromptOpen } = useIdleStore();
  const { clearParams } = useNavigateWithSecureParams();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const idToken = auth.user?.id_token;
  const [remainingTime, setRemainingTime] = useState<number>(TWO_MINUTES_IN_SECONDS);

  const onPrompt = () => {
    setIdlePromptOpen(true);
  };

  const { getRemainingTime, reset } = useIdleTimer({
    onPrompt,
    timeout,
    promptBeforeIdle: TWO_MINUTES_IN_SECONDS * 1000,
    throttle: 500,
    crossTab: true,
    events: ['keydown', 'wheel', 'DOMMouseScroll', 'mousewheel', 'mousedown', 'touchstart', 'touchmove'],
  });

  const handleLogout = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [AGREEMENTS_KEY, PROFILE_PICTURE_KEY, USER_KEY] });
    window.sessionStorage.clear();
    clearParams();
    auth.removeUser();
    if (idToken) auth.signoutRedirect({ id_token_hint: idToken });
  }, [auth, idToken, queryClient]);

  // Keep user logged in
  const handleKeepLoggedIn = useCallback(() => {
    setIdlePromptOpen(false);
  }, [setIdlePromptOpen]);

  // Update remaining time at regular intervals
  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime(Math.ceil(getRemainingTime() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [getRemainingTime]);

  // Automatically log out when time runs out
  useEffect(() => {
    if (remainingTime === 0) {
      if (idlePromptOpen) {
        handleLogout();
      } else {
        reset();
      }
    }
  }, [remainingTime, handleLogout]);

  return (
    <Modal
      visible={idlePromptOpen}
      title="Are you still there?"
      displayCloseIcon={false}
      modalClassName={styles['inactivity-modal']}
    >
      <div className={styles['inactivity-modal-content']}>
        <p className={styles['inactivity-prompt-message']}>
          Due to inactivity, you will automatically be logged out of Cobalt in {formatSeconds(remainingTime)} minutes.
        </p>
        <div className="flex gap-[16px]">
          <Button onClick={handleKeepLoggedIn}>KEEP ME LOGGED IN</Button>
          <Button onClick={handleLogout}>LOG OUT</Button>
        </div>
      </div>
    </Modal>
  );
};

export default InactivityPrompt;
